import { useMemo, useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useOpsPage } from '../hooks/useDailyInstallations';
import {
  BarRow, Note, PageHeader, Panel, States, Stat,
  num, pct, selectClass, shortDate, thisMonth,
} from '../components/OpsPageKit';

// Safety & Compliance — the NEO2 rebuild.
//
// MEWP work that was approved without a site-specific risk assessment on
// record for the address. This is the one page here with a safety consequence,
// so it lists the actual jobs rather than only counting them: a number nobody
// can act on is not a compliance control.
function SafetyCompliance() {
  const [month, setMonth] = useState(thisMonth());
  const [region, setRegion] = useState('');

  const filters = useMemo(() => ({ month, region }), [month, region]);
  const {
    data, isLoading, isFetching, refetch, isError,
  } = useOpsPage('safety-compliance', filters);

  const t = data?.totals || {};
  const c = data?.caveats || {};
  const regions = (data?.by_region || []).map((r) => r.region);
  const clean = t.missing_ssra === 0;

  return (
    <div className="p-6">
      <PageHeader
        title="Safety & Compliance"
        subtitle="MEWP work approved without a site-specific risk assessment on record."
        month={month}
        onMonth={setMonth}
        onRefresh={refetch}
        fetching={isFetching}
      >
        <select value={region} onChange={(e) => setRegion(e.target.value)} className={selectClass}>
          <option value="">All regions</option>
          {regions.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </PageHeader>

      <States isLoading={isLoading} isError={isError} what="safety and compliance" />

      {data && (
        <>
          <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat label="MEWP approved" value={num(t.approved_mewp)} sub="in the month" />
            <Stat
              label="No SSRA found"
              value={num(t.missing_ssra)}
              sub="approved without one"
              tone={clean ? '#16A34A' : '#DC2626'}
              accent={!clean}
            />
            <Stat
              label="Compliance"
              value={pct(t.compliance_rate)}
              tone={t.compliance_rate >= 0.98 ? '#16A34A' : '#D97706'}
            />
            <Stat
              label="SSRAs recorded"
              value={num(t.ssras_recorded)}
              sub={`${num(t.ssra_addresses)} addresses`}
            />
          </div>

          {!clean ? (
            <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
              <p className="text-xs text-red-800">
                <strong>
                  {num(t.missing_ssra)} MEWP {t.missing_ssra === 1 ? 'job was' : 'jobs were'} approved
                  with no SSRA recorded for the address.
                </strong>{' '}
                Each one is listed below so it can be chased or the assessment matched up. Pole
                surveys are exempt and already excluded ({num(t.pole_surveys_exempt)} this month).
              </p>
            </div>
          ) : null}

          {data.by_region.length ? (
            <div className="mt-4">
              <Panel title="Where the gaps are">
                <div className="py-1">
                  {data.by_region.map((r) => (
                    <BarRow
                      key={r.region}
                      label={r.region}
                      value={r.n}
                      total={Math.max(1, ...data.by_region.map((x) => x.n))}
                      colour="#DC2626"
                    />
                  ))}
                </div>
              </Panel>
            </div>
          ) : null}

          <div className="mt-4">
            <Panel
              title="MEWP approvals with no SSRA"
              right={<span className="text-xs text-gray-400">{num(data.missing.length)} listed</span>}
            >
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Engineer</th>
                      <th className="px-3 py-2">Address</th>
                      <th className="px-3 py-2">Reason</th>
                      <th className="px-3 py-2">Region</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.missing.map((r) => (
                      <tr key={r.id} className="hover:bg-gray-50">
                        <td className="px-3 py-1.5 whitespace-nowrap tabular-nums text-gray-500">
                          {shortDate(r.date)}
                        </td>
                        <td className="px-3 py-1.5 font-medium text-gray-800">{r.engineer || '—'}</td>
                        <td className="px-3 py-1.5 max-w-md truncate text-gray-600" title={r.address}>
                          {r.address}
                        </td>
                        <td className="px-3 py-1.5 text-xs text-gray-500">{r.reason || '—'}</td>
                        <td className="px-3 py-1.5 text-xs text-gray-500">{r.region || '—'}</td>
                      </tr>
                    ))}
                    {!data.missing.length ? (
                      <tr>
                        <td colSpan={5} className="px-3 py-10 text-center text-gray-400">
                          Every approved MEWP job this month has an SSRA on record.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>

          <Note>
            <p>{c.address_match} Worth checking a listed address against the SSRA list before
              treating it as a real gap.
            </p>
            <p className="mt-1">
              The report also counted completed jobs with no attachment. The job mirror does not
              carry the attachment flag, so that measure is absent here rather than approximated.
            </p>
          </Note>
        </>
      )}
    </div>
  );
}

export default SafetyCompliance;
